// ./src/components/features/DisplayLayouts/ComponentsEditor/ComponentsEditor.tsx
import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  Button,
  Switch,
  ActionIcon,
  Paper,
  Menu,
  Collapse,
  Box,
} from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { AppIcon } from "@/components/generic";
import { DateTimeFormatSelector } from "@/components/generic";
import { ComponentType } from "../../DisplayScreen/displayScreenTypes";

export interface ComponentItem {
  type: ComponentType;
  visible: boolean;
  format?: string; // For time component
}

export interface ComponentsEditorProps {
  /** Current components array */
  value: ComponentItem[] | null | undefined;

  /** Callback when components change */
  onChange: (value: ComponentItem[]) => void;

  /** Label for the editor */
  label?: string;

  /** Description text */
  description?: string;
}

const COMPONENT_LABELS: Record<ComponentType, string> = {
  logo: "Logo",
  room_name: "Room Name",
  venue_name: "Venue Name",
  display_name: "Display Name",
  time: "Date/Time",
  running_messages: "Running Messages",
};

const COMPONENT_ICONS: Record<ComponentType, string> = {
  logo: "Photo",
  room_name: "Door",
  venue_name: "Building",
  display_name: "DeviceDesktop",
  time: "Clock",
  running_messages: "Messages",
};

export const ComponentsEditor: React.FC<ComponentsEditorProps> = ({
  value,
  onChange,
  label,
  description,
}) => {
  const components = value || [];
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Get available component types (not already added)
  const getAvailableTypes = (): ComponentType[] => {
    const usedTypes = new Set(components.map((c) => c.type));
    const allTypes: ComponentType[] = [
      "logo",
      "venue_name",
      "room_name",
      "display_name",
      "time",
      "running_messages",
    ];
    return allTypes.filter((type) => !usedTypes.has(type));
  };

  // Handle drag end
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onChange(items);

    // Update expanded index if needed
    if (expandedIndex === result.source.index) {
      setExpandedIndex(result.destination.index);
    }
  };

  // Add new component
  const handleAddComponent = (type: ComponentType) => {
    const newComponent: ComponentItem = {
      type,
      visible: true,
      ...(type === "time" && { format: "h:mm A" }), // Default format for time
    };
    onChange([...components, newComponent]);
  };

  // Remove component
  const handleRemoveComponent = (index: number) => {
    const items = components.filter((_, i) => i !== index);
    onChange(items);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  // Toggle visibility
  const handleToggleVisible = (index: number) => {
    const items = [...components];
    items[index] = { ...items[index], visible: !items[index].visible };
    onChange(items);
  };

  // Update time format
  const handleFormatChange = (index: number, format: string | null) => {
    const items = [...components];
    items[index] = { ...items[index], format: format || undefined };
    onChange(items);
  };

  // Toggle expanded state
  const handleToggleExpanded = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const availableTypes = getAvailableTypes();

  console.log("getAvailableTypes", getAvailableTypes());
  console.log("components", components);

  return (
    <Stack gap="sm">
      {label && (
        <div>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {description && (
            <Text size="xs" c="dimmed" mt={2}>
              {description}
            </Text>
          )}
        </div>
      )}

      {/* Components list */}
      {components.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="components-list">
            {(provided) => (
              <Stack
                gap="xs"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {components.map((component, index) => (
                  <Draggable
                    key={`${component.type}-${index}`}
                    draggableId={`${component.type}-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        withBorder
                        p="sm"
                        style={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging
                            ? "var(--mantine-color-blue-0)"
                            : undefined,
                        }}
                      >
                        <Stack gap="sm">
                          <Group justify="space-between" wrap="nowrap">
                            {/* Drag handle and component info */}
                            <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                              <div {...provided.dragHandleProps}>
                                <AppIcon
                                  iconName="GripVertical"
                                  size={16}
                                  style={{ cursor: "grab", color: "gray" }}
                                />
                              </div>
                              <AppIcon
                                iconName={
                                  COMPONENT_ICONS[component.type] as any
                                }
                                size={18}
                              />
                              <Text size="sm" fw={500}>
                                {COMPONENT_LABELS[component.type]}
                              </Text>
                            </Group>

                            {/* Actions */}
                            <Group gap="xs" wrap="nowrap">
                              <Switch
                                checked={component.visible}
                                onChange={() => handleToggleVisible(index)}
                                size="sm"
                                aria-label="Toggle visibility"
                              />
                              {component.type === "time" && (
                                <ActionIcon
                                  variant={
                                    expandedIndex === index
                                      ? "filled"
                                      : "subtle"
                                  }
                                  color="blue"
                                  onClick={() => handleToggleExpanded(index)}
                                  size="sm"
                                >
                                  <AppIcon
                                    iconName={
                                      expandedIndex === index
                                        ? "ChevronUp"
                                        : "ChevronDown"
                                    }
                                    size={16}
                                  />
                                </ActionIcon>
                              )}
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleRemoveComponent(index)}
                                size="sm"
                              >
                                <AppIcon iconName="Trash" size={16} />
                              </ActionIcon>
                            </Group>
                          </Group>

                          {/* Time component settings */}
                          {component.type === "time" && (
                            <Collapse in={expandedIndex === index}>
                              <Box
                                pl="xl"
                                pt="xs"
                                style={(theme) => ({
                                  borderLeft: `2px solid ${theme.colors.gray[3]}`,
                                })}
                              >
                                <DateTimeFormatSelector
                                  label="Format"
                                  value={component.format || null}
                                  onChange={(format) =>
                                    handleFormatChange(index, format)
                                  }
                                  size="sm"
                                />
                              </Box>
                            </Collapse>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Paper withBorder p="md">
          <Text size="sm" c="dimmed" ta="center">
            No components added. Click "Add Component" to get started.
          </Text>
        </Paper>
      )}

      {/* Add component button */}
      {availableTypes.length > 0 && (
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button
              variant="light"
              leftSection={<AppIcon iconName="Plus" size={16} />}
              size="sm"
            >
              Add Component
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Available Components</Menu.Label>
            {availableTypes.map((type) => (
              <Menu.Item
                key={type}
                leftSection={
                  <AppIcon iconName={COMPONENT_ICONS[type] as any} size={16} />
                }
                onClick={() => handleAddComponent(type)}
              >
                {COMPONENT_LABELS[type]}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Stack>
  );
};
